import { useRef, useState } from "react";
import { Link } from "@inertiajs/react";
import { useTheme } from "@/hooks/useTheme";
import { useDismiss } from "@/hooks/useDismiss";
import { MoonIcon } from "@/components/ui/icons/MoonIcon";
import { SunIcon } from "@/components/ui/icons/SunIcon";
import { MimirRune } from "@/components/ui/MimirRune";
import { SkipLink } from "@/components/ui/SkipLink";

export function Nav() {
  const { dark, toggleDark } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const closeMenu = () => {
    setMenuOpen(false);
    triggerRef.current?.focus();
  };

  useDismiss(navRef, closeMenu, menuOpen);

  const themeToggleClass =
    "flex items-center justify-center w-11 h-11 rounded-md text-nav-toggle hover:text-nav-toggle-hover transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

  const navLink =
    "font-mono text-xs uppercase tracking-[0.16em] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded py-2 px-1";

  return (
    <>
      <SkipLink />
      <nav
        ref={navRef}
        className="relative max-w-5xl mx-auto px-4 sm:px-8 py-5 [padding-top:max(1.25rem,env(safe-area-inset-top))] border-b border-line-soft"
      >
        <div className="flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center gap-3 font-display text-base uppercase tracking-[0.34em] text-nav-logo hover:text-heading transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
          >
            <MimirRune />
            Mímir
          </Link>

          <div className="flex items-center gap-3 sm:gap-6">
            {/* Desktop links */}
            <div className="hidden sm:flex items-center gap-8">
              <a
                href="/#languages"
                className={`${navLink} text-nav-link hover:text-nav-link-hover`}
              >
                Languages
              </a>
              <Link
                href="/about"
                className={`${navLink} text-nav-link hover:text-nav-link-hover`}
              >
                About
              </Link>
              <Link
                href="/users/sign_in"
                className={`${navLink} text-em hover:text-accent`}
              >
                Sign in
              </Link>
            </div>

            <button
              onClick={toggleDark}
              className={themeToggleClass}
              aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {dark ? <SunIcon /> : <MoonIcon />}
            </button>

            {/* Mobile hamburger */}
            <button
              ref={triggerRef}
              type="button"
              onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
              aria-label="Menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-menu"
              className="sm:hidden flex items-center justify-center w-11 h-11 rounded-md text-nav-toggle hover:text-nav-toggle-hover transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                aria-hidden="true"
                focusable="false"
              >
                {menuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" />
                ) : (
                  <path d="M3 6h18M3 12h18M3 18h18" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile slide-down menu */}
        {menuOpen && (
          <div
            id="mobile-nav-menu"
            role="region"
            aria-label="Menu"
            className="sm:hidden mt-3 flex flex-col gap-2 border-t border-border-divider pt-3 motion-safe:animate-fade-in"
          >
            <a
              href="/#languages"
              onClick={() => setMenuOpen(false)}
              className={`${navLink} flex items-center min-h-11 text-nav-link hover:text-nav-link-hover`}
            >
              Languages
            </a>
            <Link
              href="/about"
              onClick={() => setMenuOpen(false)}
              className={`${navLink} flex items-center min-h-11 text-nav-link hover:text-nav-link-hover`}
            >
              About
            </Link>
            <Link
              href="/users/sign_in"
              onClick={() => setMenuOpen(false)}
              className={`${navLink} flex items-center min-h-11 text-em hover:text-accent`}
            >
              Sign in
            </Link>
            <Link
              href="/users/sign_up"
              onClick={() => setMenuOpen(false)}
              className={`${navLink} flex items-center min-h-11 text-em hover:text-accent`}
            >
              Sign up
            </Link>
          </div>
        )}
      </nav>
    </>
  );
}
